/**
 * Watches tab visibility and reports transitions.
 *
 * Extracted so connection-owning services can suspend work while nobody is
 * looking at the page. No-ops outside a DOM environment (SSR, node tests
 * without jsdom), so callers never need to guard.
 */
export class VisibilityWatcher {
  private listener: (() => void) | null = null

  /** True only when we can observe visibility AND the tab is hidden. */
  get isHidden(): boolean {
    return typeof document !== 'undefined' && document.visibilityState === 'hidden'
  }

  /** Idempotent — a second start() with a listener already attached is a no-op. */
  start(handlers: { onHide: () => void; onShow: () => void }): void {
    if (this.listener || typeof document === 'undefined') return
    this.listener = () => {
      if (document.visibilityState === 'hidden') handlers.onHide()
      else handlers.onShow()
    }
    document.addEventListener('visibilitychange', this.listener)
  }

  stop(): void {
    if (this.listener && typeof document !== 'undefined') {
      document.removeEventListener('visibilitychange', this.listener)
    }
    this.listener = null
  }
}
